import React, { useState } from "react";
import { Plus, X } from "lucide-react";
import { Checkbox } from "./Checkbox";
import CustomDropdown, { type DropdownOption } from "./CustomDropdown";

/**
 * The four controls the kit's `FormControls` has no equivalent of: a list
 * somebody adds to, a number with a unit beside it, a pair of times, and a
 * question with two answers.
 *
 * They were written for the pre-consultation form and lived inside that
 * feature, on the rule that a control promoted before it has a second caller is
 * a guess about what that caller will need. The Add client form is that second
 * caller — it now asks the same questions about a client's body, food, day and
 * health that the form does — so they are here, and `PreconsultationControls`
 * re-exports them under the names that feature already refers to them by.
 */

/** How many entries a `ChipsField` takes unless its caller says otherwise. */
export const MAX_CHIPS = 5;

/** The label above a control the rest of the kit is not drawing. */
export const FieldLabel: React.FC<{
  htmlFor?: string;
  children: React.ReactNode;
  /** The `(Add upto 5)` the design sets in grey beside two of them. */
  note?: string;
}> = ({ htmlFor, children, note }) => (
  <label
    htmlFor={htmlFor}
    className="mb-1.5 flex items-baseline gap-2 text-[13px] 3xl:text-[14px] text-ink"
  >
    {children}
    {note && <span className="text-[12px] text-ink-muted">{note}</span>}
  </label>
);

const errorLine = (error?: string) =>
  error ? (
    <p role="alert" className="mt-1.5 text-[12px] text-red-500">
      {error}
    </p>
  ) : null;

/* ── A list somebody adds to ──────────────────────────────────────────── */

export interface ChipsFieldProps {
  id: string;
  label: string;
  placeholder: string;
  values: string[];
  onChange: (values: string[]) => void;
  /** How many the list takes. The box stops accepting at it rather than
   *  letting somebody type a sixth and lose it. */
  max?: number;
  /** The grey aside beside the label — `(Add upto 5)`. Given by the caller
   *  rather than built from `max`, because not every list wears one: the
   *  medications have a ceiling nobody needs told about, and a count beside a
   *  question about somebody's prescriptions reads as a limit on their health
   *  rather than on the box. */
  note?: string;
}

/**
 * The dishes and the medications: a free list somebody builds a line at a
 * time.
 *
 * Not a `MultiSelect`. That control picks from a catalog, and there is no
 * catalog of dishes a person likes or of the exact wording on a strip of
 * tablets — the whole value of these fields is that they take what the person
 * actually says. So it is a text box and a button, and what has been added
 * stands above it as chips that can be taken back off.
 *
 * Enter adds as well as the button, because a list is typed in a run and
 * reaching for a button between each is what makes somebody stop at two.
 */
export const ChipsField: React.FC<ChipsFieldProps> = ({
  id,
  label,
  placeholder,
  values,
  onChange,
  max = MAX_CHIPS,
  note,
}) => {
  const [draft, setDraft] = useState("");
  const full = values.length >= max;

  const add = () => {
    const value = draft.trim();
    if (!value || full) return;
    // The same thing twice is not two answers. Compared case-insensitively,
    // so "Roti" does not join "roti" in a list the practitioner has to read.
    const already = values.some(
      (held) => held.toLowerCase() === value.toLowerCase()
    );
    if (!already) onChange([...values, value]);
    setDraft("");
  };

  return (
    <div>
      <FieldLabel htmlFor={id} note={note}>
        {label}
      </FieldLabel>

      {values.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-2">
          {values.map((value) => (
            <li key={value}>
              <span className="inline-flex items-center gap-2 rounded-[6px] border border-[#BFEFF5] bg-[#EFFBFD] px-3 py-1.5 text-[13px] text-ink">
                {value}
                <button
                  type="button"
                  onClick={() =>
                    onChange(values.filter((held) => held !== value))
                  }
                  aria-label={`Remove ${value}`}
                  className="text-ink-muted transition-colors hover:text-ink"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-stretch gap-2">
        <input
          id={id}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            // The form is one big element and Enter would send it — from a
            // box whose whole purpose is to be typed in repeatedly.
            event.preventDefault();
            add();
          }}
          placeholder={placeholder}
          disabled={full}
          className={
            "min-w-0 flex-1 rounded-[6px] border border-line bg-white px-3.5 py-2.5 " +
            "text-[14px] text-ink placeholder:text-ink-subtle " +
            "focus:border-brand focus:outline-none disabled:cursor-not-allowed disabled:bg-[#F7F9FA]"
          }
        />
        <button
          type="button"
          onClick={add}
          disabled={full || !draft.trim()}
          aria-label={`Add to ${label}`}
          className={
            "flex w-[46px] flex-shrink-0 items-center justify-center rounded-[6px] " +
            "bg-[#D7F9FD] text-[#00899B] transition-colors hover:bg-[#BFEFF5] " +
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 " +
            "disabled:cursor-not-allowed disabled:opacity-60"
          }
        >
          <Plus size={20} aria-hidden="true" />
        </button>
      </div>

      {full && (
        <p className="mt-1.5 text-[12px] text-ink-muted">
          That’s {max} — remove one to add another.
        </p>
      )}
    </div>
  );
};

/* ── A number with its unit ───────────────────────────────────────────── */

export interface MeasureFieldProps<Unit extends string> {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onValueChange: (value: string) => void;
  unit: Unit;
  units: readonly Unit[];
  onUnitChange: (unit: Unit) => void;
  error?: string;
}

/**
 * A height or a weight: the figure in a box, the unit in a small select beside
 * it.
 *
 * The unit is the app's own dropdown, not a native `<select>`. A native one is
 * quicker to open on a phone, but it is drawn by the operating system: a blue
 * highlight, the system font, and a menu that looks like nothing else on the
 * screen — sitting directly beside a box this app styled itself.
 *
 * The box is `inputMode="decimal"`, so a phone offers the number pad. A height
 * in feet is `5.4`, so the point has to be on that keyboard.
 */
export function MeasureField<Unit extends string>({
  id,
  label,
  placeholder,
  value,
  onValueChange,
  unit,
  units,
  onUnitChange,
  error,
}: MeasureFieldProps<Unit>) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="flex items-stretch gap-3">
        <input
          id={id}
          value={value}
          onChange={(event) => {
            // Digits and one point. Anything else is dropped as it is typed
            // rather than complained about afterwards — there is no weight
            // with a letter in it, so the keystroke is a slip.
            const next = event.target.value.replace(/[^\d.]/g, "");
            if (/^\d*\.?\d*$/.test(next)) onValueChange(next);
          }}
          inputMode="decimal"
          placeholder={placeholder}
          aria-invalid={!!error}
          className={
            "min-w-0 flex-1 rounded-[6px] border bg-white px-3.5 py-2.5 " +
            "text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none " +
            (error ? "border-red-400" : "border-line focus:border-brand")
          }
        />
        {/* The list is two rows and the trigger is a third of the width of a
            phone, so the panel is left to size itself rather than being held
            to the trigger: it is anchored and drawn in a portal, which is what
            keeps it clear of the card's own rounding and overflow. */}
        <CustomDropdown
          options={units.map((option) => ({ value: option, label: option }))}
          value={unit}
          onChange={(next) => onUnitChange(next as Unit)}
          aria-label={`${label} unit`}
          className="w-[88px] flex-shrink-0"
        />
      </div>
      {errorLine(error)}
    </div>
  );
}

/* ── A pair of times ──────────────────────────────────────────────────── */

export interface TimeRangeFieldProps {
  id: string;
  label: string;
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  /** Ticked, the two boxes are switched off and no time is sent at all. */
  anyHours: boolean;
  onAnyHoursChange: (value: boolean) => void;
}

/**
 * `09:00` as `09:00 am` — the twelve-hour clock a working day is spoken about
 * on here, and what the client's detail screen already writes these times as.
 * Anything that is not a time of day comes back untouched, so an odd value
 * off an older record is still shown rather than swallowed.
 */
export const timeLabel = (value: string): string => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return value;

  const hours = Number(match[1]);
  if (!Number.isFinite(hours) || hours > 23) return value;

  const suffix = hours < 12 ? "am" : "pm";
  // Midnight and noon are both 12 on a twelve-hour clock — `0 % 12` is 0.
  const shown = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(shown).padStart(2, "0")}:${match[2]} ${suffix}`;
};

/**
 * Every half hour of a day. Nobody describes the hours they work or sleep to
 * the minute, and a list of 1,440 would be scrolled past rather than read —
 * half-hourly is 48 rows, which is the granularity these answers are actually
 * given at.
 *
 * The value stays `HH:MM` on a 24-hour clock, which is what the payloads and
 * the record already speak; only the label is twelve-hour.
 */
const HALF_HOURS: DropdownOption[] = Array.from({ length: 48 }, (_, slot) => {
  const value =
    `${String(Math.floor(slot / 2)).padStart(2, "0")}:` +
    (slot % 2 === 0 ? "00" : "30");
  return { value, label: timeLabel(value) };
});

/**
 * The list, with whatever the field is already holding on it.
 *
 * A record written before this control existed can hold `09:15`, and a closed
 * dropdown shows its placeholder for a value no option matches — which would
 * read as an empty field, and be saved back as one. So an off-grid time joins
 * the list in its own place: `HH:MM` sorts as text exactly as it does as a
 * time, so it lands where it belongs in the day.
 */
const timeOptions = (value: string): DropdownOption[] => {
  if (!value || HALF_HOURS.some((option) => option.value === value)) {
    return HALF_HOURS;
  }
  const held = { value, label: timeLabel(value) };
  const before = HALF_HOURS.filter((option) => option.value < value);
  return [...before, held, ...HALF_HOURS.slice(before.length)];
};

/**
 * Working hours, and sleep.
 *
 * Two dropdowns rather than `<input type="time">`. The native control is drawn
 * by the operating system — its own font, its own spinner, its own idea of
 * whether the day has twelve hours or twenty-four — sitting in a form where
 * every other control is this app's. The list also settles the question the
 * native box leaves to the browser's locale: these are read as `09:00 am`
 * here, the same as on the client's detail screen.
 *
 * "No fixed hours" is a real answer rather than a way of skipping the
 * question. Shift work, a newborn, a freelance week — a practitioner planning
 * meals around a day needs to be told there is no shape to it, and an empty
 * pair of boxes would only say nobody got that far.
 */
export const TimeRangeField: React.FC<TimeRangeFieldProps> = ({
  id,
  label,
  from,
  to,
  onFromChange,
  onToChange,
  anyHours,
  onAnyHoursChange,
}) => (
  <div>
    <FieldLabel htmlFor={`${id}-from`}>{label}</FieldLabel>
    <div className="grid grid-cols-2 gap-3">
      {[
        { key: "from", value: from, onChange: onFromChange, what: "from" },
        { key: "to", value: to, onChange: onToChange, what: "to" },
      ].map((box) => (
        <CustomDropdown
          key={box.key}
          id={`${id}-${box.key}`}
          options={timeOptions(box.value)}
          value={box.value}
          onChange={box.onChange}
          disabled={anyHours}
          // Sentence case, because the placeholder is what the closed box
          // reads as: "Working hours" above, "From" and "To" inside it.
          placeholder={box.what === "from" ? "From" : "To"}
          aria-label={`${label} ${box.what}`}
        />
      ))}
    </div>
    <div className="mt-2">
      <Checkbox
        label="No fixed hours"
        checked={anyHours}
        onCheckedChange={(checked) => {
          onAnyHoursChange(checked);
          // The times go with the tick. Left behind they would be sent as an
          // answer that contradicts the one just given.
          if (checked) {
            onFromChange("");
            onToChange("");
          }
        }}
      />
    </div>
  </div>
);

/* ── A question with two answers ──────────────────────────────────────── */

export interface ChoiceFieldProps<Value extends string> {
  id: string;
  label: string;
  value: Value | "";
  options: readonly { value: Value; icon?: React.ReactNode }[];
  onChange: (value: Value) => void;
  error?: string;
  /**
   * How much of the field the pair takes.
   *
   * `full` lines the pair up with the control beside it, which is what a form
   * of one question per row wants. `half` is for a question sitting in a grid
   * of wide fields, where two buttons stretched to the width of a dropdown
   * read as the biggest thing on the row rather than the smallest — Yes and No
   * are two short words and do not need 400px to say so.
   *
   * Only from `sm` up: on a phone the field is the width of the screen, and
   * half of that is a button somebody has to aim at.
   */
  width?: "full" | "half";
  /**
   * Shown, but not the reader's to change — a question somebody else has
   * already answered on their behalf.
   *
   * The picked one keeps its tint rather than greying out with the pair: it is
   * still the answer, and a locked field whose answer is unreadable is worse
   * than no field. It is only the *other* button that goes flat, because that
   * is the one nothing will happen on.
   */
  disabled?: boolean;
}

/**
 * Two buttons rather than a dropdown, for a question that has two answers.
 *
 * A list of two is not a list worth opening: the pair costs one tap where a
 * dropdown costs two, and both answers are readable without anything being
 * dismissed — which matters more on a phone than anywhere else.
 */
export function ChoiceField<Value extends string>({
  id,
  label,
  value,
  options,
  onChange,
  error,
  width = "full",
  disabled = false,
}: ChoiceFieldProps<Value>) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-[13px] 3xl:text-[14px] text-ink">
        {label}
      </legend>
      {/* The floor is what stops the halving from running away with it: in a
          narrow column half a cell is not wide enough for an icon and a word,
          and a button its own label spills out of is worse than a wide one. */}
      <div
        className={
          "grid grid-cols-2 gap-3" +
          (width === "half" ? " sm:w-1/2 sm:min-w-[180px]" : "")
        }
      >
        {options.map((option, index) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              // Only the first carries the id: it is what a form scrolls to
              // when this is the question that was left unanswered.
              id={index === 0 ? id : undefined}
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={
                // The same box every other control in the row is drawn in —
                // radius, padding and type scale — so the pair sits at exactly
                // their height instead of standing a few pixels taller.
                "flex items-center justify-center gap-2 rounded-[6px] border " +
                "px-3.5 py-2.5 3xl:py-3 text-[14px] 3xl:text-[15px] " +
                "transition-colors " +
                (disabled ? "cursor-not-allowed " : "") +
                (selected
                  ? "border-brand bg-brand-soft text-ink"
                  : disabled
                  ? "border-line bg-[#F7F9FA] text-ink-muted"
                  : "border-line bg-white text-ink hover:border-ink-subtle")
              }
            >
              {option.icon && <span className="text-brand">{option.icon}</span>}
              {option.value}
            </button>
          );
        })}
      </div>
      {errorLine(error)}
    </fieldset>
  );
}
